import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { api } from '@/lib/api'
import { API_BASE_URL } from '@/lib/config'
import { useAuthStore } from '@/stores/useAuthStore'
import type { Contact, ContactStatus } from '@/types/contacts'

interface ContactsResponse {
  contacts: Contact[]
}

interface ContactsPageResponse {
  contacts: Contact[]
  total: number
  has_more: boolean
}

export function useContacts(archived = false) {
  return useQuery({
    queryKey: ['contacts', archived],
    queryFn: async () => {
      const { data } = await api.get<ContactsResponse>('/api/contacts/', {
        params: archived ? { archived: 'true' } : undefined,
      })
      return data.contacts || []
    },
  })
}

export const CONTACTS_PAGE_SIZE = 50

/**
 * Liste paginée avec recherche côté serveur (page Contacts).
 * `useContacts` (liste complète) reste pour la résolution de noms
 * (useContactsMap) — ne pas confondre les deux.
 */
/** Ordres de la liste des contacts (paramètre ?sort= du serveur). */
export type ContactsSort = 'recent' | 'created' | 'name' | 'company'

/** Filtres du carnet (page Contacts) : étiquette, statut, origine. */
export interface ContactsFilters {
  tag?: string
  status?: string
  source?: string
}

/** Which part of the address book is shown: active contacts, archived ones, or the trash. */
export type ContactsView = 'active' | 'archived' | 'deleted'

/** Paramètres communs à la liste et à l'export : seuls les filtres actifs partent. */
export function paramsFiltres(search: string, view: ContactsView, filters: ContactsFilters = {}) {
  return {
    ...(search.trim() ? { q: search.trim() } : {}),
    ...(view === 'archived' ? { archived: 'true' } : {}),
    ...(view === 'deleted' ? { deleted: 'true' } : {}),
    ...(filters.tag ? { tag: filters.tag } : {}),
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.source ? { source: filters.source } : {}),
  }
}

export function useContactsPaginated(
  search = '',
  view: ContactsView = 'active',
  sort: ContactsSort = 'recent',
  filters: ContactsFilters = {},
) {
  const { tag = '', status = '', source = '' } = filters
  return useInfiniteQuery({
    queryKey: ['contacts', 'paginated', { search, view, sort, tag, status, source }],
    queryFn: async ({ pageParam }) => {
      const { data } = await api.get<ContactsPageResponse>('/api/contacts/', {
        params: {
          limit: CONTACTS_PAGE_SIZE,
          offset: pageParam,
          ...paramsFiltres(search, view, { tag, status, source }),
          ...(sort !== 'recent' ? { sort } : {}),
        },
      })
      return data
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.has_more ? allPages.length * CONTACTS_PAGE_SIZE : undefined,
  })
}

/**
 * Ce que le composeur envoie au serveur pour une saisie « nom ou numéro » :
 * un numéro est cherché sur ses chiffres (la mise en forme ne gêne pas), un
 * nom sur son premier mot (le serveur cherche prénom ou nom séparément ; le
 * composeur filtre ensuite sur tous les mots).
 */
export function requeteSuggestions(saisie: string): string {
  const s = saisie.trim()
  if (/^[\d\s()+.-]+$/.test(s)) return s.replace(/\D/g, '')
  return s.split(/\s+/)[0] ?? ''
}

/** Suggestions du composeur : six contacts au plus, recherche serveur. */
export function useSuggestionsContacts(saisie: string) {
  const q = requeteSuggestions(saisie)
  return useQuery({
    queryKey: ['contacts', 'suggestions', q],
    queryFn: async () => {
      const { data } = await api.get<ContactsPageResponse>('/api/contacts/', {
        params: { q, limit: 6 },
      })
      return data.contacts
    },
    enabled: q.length >= 2,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  })
}

// =============================================================================
// Carnet : facettes des filtres, actions groupées, export CSV
// =============================================================================

export interface ContactFacet {
  value: string
  label?: string
  count: number
}

export interface ContactFacets {
  total: number
  archived: number
  /** Contacts in the trash (erased for good after 30 days). */
  deleted: number
  tags: ContactFacet[]
  statuses: ContactFacet[]
  sources: ContactFacet[]
}

/** GET /api/contacts/facets/ : valeurs des filtres avec leur nombre de contacts. */
export function useContactFacets() {
  return useQuery({
    // Sous ['contacts'] : toute invalidation du carnet relit aussi les facettes.
    queryKey: ['contacts', 'facets'],
    queryFn: async () => {
      const { data } = await api.get<ContactFacets>('/api/contacts/facets/')
      return data
    },
    staleTime: 30_000,
  })
}

/** `delete` moves to the trash, `undelete` takes out of it. */
export type BulkContactsAction = 'add_tag' | 'remove_tag' | 'set_status' | 'archive' | 'restore' | 'delete' | 'undelete'

/**
 * Either the checked contacts (`phones`, 1,000 at most) or every contact of the
 * current filter (`all`, Gmail style). `expected` is the count shown to the user:
 * the server refuses with 409 if the list changed in the meantime.
 */
export type BulkContactsInput = {
  action: BulkContactsAction
  value?: string
} & (
  | { phones: string[] }
  | { all: true; filters: ReturnType<typeof paramsFiltres>; expected: number }
)

/** POST /api/contacts/bulk/ : `updated` done now, or `queued` done in the background. */
export function useBulkContacts() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: BulkContactsInput) => {
      const { data } = await api.post<{ updated?: number; queued?: number }>('/api/contacts/bulk/', input)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
      queryClient.invalidateQueries({ queryKey: ['contact'] })
      queryClient.invalidateQueries({ queryKey: ['inbox'] })
    },
  })
}

/** Télécharge le CSV du carnet selon les filtres en cours (gestionnaire seulement). */
export async function exportContactsCsv(search: string, view: ContactsView, filters: ContactsFilters = {}) {
  const { data } = await api.get<Blob>('/api/contacts/export/', {
    params: paramsFiltres(search, view, filters),
    responseType: 'blob',
  })
  const url = URL.createObjectURL(data)
  const a = document.createElement('a')
  a.href = url
  a.download = `contacts-${new Date().toLocaleDateString('en-CA')}.csv`
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export function useContact(phoneNumber: string | null, notesLimit = 5) {
  return useQuery({
    queryKey: ['contact', phoneNumber, notesLimit],
    queryFn: async () => {
      if (!phoneNumber) return null
      const { data } = await api.get<Contact>(
        `/api/contacts/${encodeURIComponent(phoneNumber)}/`,
        { params: { notes_limit: notesLimit } },
      )
      return data
    },
    enabled: !!phoneNumber,
  })
}

interface UpdateContactInput {
  first_name?: string
  last_name?: string
  email?: string
  company_name?: string
  job_title?: string
  status?: ContactStatus
  secondary_phone?: string
  address?: string
  website?: string
  tags?: string[]
  custom_fields?: Record<string, string | number | boolean | null>
}

interface CreateContactInput {
  phone_number: string
  first_name?: string
  last_name?: string
  email?: string
  company_name?: string
  job_title?: string
  status?: string
  address?: string
}

export function useCreateContact() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateContactInput) => {
      const { data } = await api.put<Contact>(
        `/api/contacts/${encodeURIComponent(input.phone_number)}/`,
        input,
      )
      return data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
      if (data.phone_number) {
        queryClient.invalidateQueries({
          queryKey: ['contact', data.phone_number],
        })
      }
    },
  })
}

export function useUpdateContact(phoneNumber: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: UpdateContactInput) => {
      const { data } = await api.put<Contact>(
        `/api/contacts/${encodeURIComponent(phoneNumber)}/`,
        input,
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact', phoneNumber] })
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
    },
  })
}

export function useAddContactNote(phoneNumber: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (content: string) => {
      const { data } = await api.post(
        `/api/contacts/${encodeURIComponent(phoneNumber)}/notes/`,
        { content },
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact', phoneNumber] })
    },
  })
}

export function useArchiveContact(phoneNumber: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await api.post(`/api/contacts/${encodeURIComponent(phoneNumber)}/archive/`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact', phoneNumber] })
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
    },
  })
}

/** DELETE /api/contacts/{phone}/ : to the trash for 30 days (managers only). */
export function useDeleteContact(phoneNumber: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await api.delete(`/api/contacts/${encodeURIComponent(phoneNumber)}/`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact', phoneNumber] })
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
      queryClient.invalidateQueries({ queryKey: ['inbox'] })
    },
  })
}

export function useRestoreContact(phoneNumber: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await api.post(`/api/contacts/${encodeURIComponent(phoneNumber)}/restore/`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact', phoneNumber] })
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
    },
  })
}

/** Upload avatar via FormData (axios accepte directement). */
export function useUploadAvatar(phoneNumber: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (file: File) => {
      const token = useAuthStore.getState().accessToken
      const formData = new FormData()
      formData.append('avatar', file)
      const res = await fetch(
        `${API_BASE_URL}/api/contacts/${encodeURIComponent(phoneNumber)}/avatar/`,
        {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: formData,
        },
      )
      if (!res.ok) throw new Error(`upload failed: ${res.status}`)
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact', phoneNumber] })
    },
  })
}

export function useDeleteAvatar(phoneNumber: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await api.delete(`/api/contacts/${encodeURIComponent(phoneNumber)}/avatar/`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact', phoneNumber] })
    },
  })
}

// =============================================================================
// Import de contacts en masse (CSV ou Excel lu dans le navigateur)
// =============================================================================

export interface ImportContactsResult {
  dry_run: boolean
  to_create: number
  to_complete: number
  already_there: number
  duplicates_in_file: number
  rejected: number
  errors: { ligne: number; code: 'missing_phone' | 'invalid_phone'; valeur: string }[]
  recognized_columns: string[]
  ignored_columns: string[]
  /** Colonnes reprises dans une propriété personnalisée existante. */
  property_columns: string[]
  /** Colonnes qui deviendront de nouvelles propriétés. */
  new_properties: string[]
}

/** POST /api/contacts/import/ : aperçu (dry_run) puis import réel. */
export function useImportContacts() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { rows: Record<string, string>[]; dry_run: boolean; new_properties?: string[] }) => {
      const { data } = await api.post<ImportContactsResult>('/api/contacts/import/', input)
      return data
    },
    onSuccess: (data) => {
      if (!data.dry_run) {
        queryClient.invalidateQueries({ queryKey: ['contacts'] })
        queryClient.invalidateQueries({ queryKey: ['contact'] })
        // L'import a pu créer des propriétés de fiche.
        if (data.new_properties.length) queryClient.invalidateQueries({ queryKey: ['contact-properties'] })
      }
    },
  })
}

// =============================================================================
// Propositions de fiche : valeurs entendues dans un appel, à approuver
// =============================================================================

export interface ContactSuggestion {
  id: number
  /** Un champ du contact (« job_title ») ou une propriété (« prop:12 »). */
  field: string
  value: string | number | boolean
  /** La phrase entendue. */
  quote: string
  call_id: string | null
  created_at: string
}

export function useContactSuggestions(phoneNumber: string | null) {
  return useQuery({
    queryKey: ['contact-suggestions', phoneNumber],
    queryFn: async () => {
      const { data } = await api.get<{ suggestions: ContactSuggestion[] }>(
        `/api/contacts/${encodeURIComponent(phoneNumber || '')}/suggestions/`,
      )
      return data.suggestions
    },
    enabled: !!phoneNumber,
    staleTime: 30_000,
  })
}

/** Approuver (remplit le champ) ou refuser une proposition. */
export function useDecideSuggestion(phoneNumber: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, accept }: { id: number; accept: boolean }) => {
      const { data } = await api.post<{ status: string }>(
        `/api/contact-suggestions/${id}/${accept ? 'accept' : 'reject'}/`,
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact-suggestions', phoneNumber] })
      queryClient.invalidateQueries({ queryKey: ['contact', phoneNumber] })
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
    },
  })
}

/** Approuver toutes les propositions en attente d'un contact. */
export function useAcceptAllSuggestions(phoneNumber: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ accepted: number; expired: number }>(
        `/api/contacts/${encodeURIComponent(phoneNumber)}/suggestions/accept-all/`,
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact-suggestions', phoneNumber] })
      queryClient.invalidateQueries({ queryKey: ['contact', phoneNumber] })
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
    },
  })
}
